import type { SeoScoreContext } from '../context';
import {
  countExactPhrase,
  countKeyword,
  termCoverage,
  textContainsKeyword,
  textStems,
  type KeywordMatcher,
} from '../keyword-match';
import { fail, pass, pct, warn, type SeoRule } from '../rule';
import { T } from '../thresholds';
import { normalizeFr } from '../tokenize-fr';
import { SeoRuleKey, SeoScoreGroup } from '../types';

const G = SeoScoreGroup.KEYWORD;

/**
 * Position (0-1) de la première occurrence dans une suite de racines. `loose` : matching souple
 * (n-1 racines pour un mot-clé de 3 racines ou plus), comme les intertitres et l'intro.
 */
function firstPositionRatio(stems: string[], matcher: KeywordMatcher, loose = false): number | null {
  const n = stems.length;
  for (let i = 0; i < n; i++) {
    if (countKeyword(stems.slice(i, i + matcher.window), matcher, loose) > 0) return n === 0 ? 0 : i / n;
  }
  return null;
}

const headingsWithKeyword = (ctx: SeoScoreContext, matcher: KeywordMatcher): string[] =>
  ctx.headings.filter((h): boolean => textContainsKeyword(h.text, matcher, true)).map((h): string => h.text);

export const KEYWORD_RULES: SeoRule[] = [
  {
    key: SeoRuleKey.KW_IN_TITLE,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      if (!ctx.matcher) return null;
      if (ctx.title.length === 0) return fail('—', 'présent');
      const stems = textStems(ctx.title);
      // Souple depuis 2026.09.5 : la requête brute (« mèches balayage différence ») ne se glisse pas
      // telle quelle dans un titre correct ; l'exiger poussait les reprises à la plaquer dans le texte.
      const ratio = firstPositionRatio(stems, ctx.matcher, true);
      if (ratio === null) return fail('absent', 'présent', [ctx.title]);
      return ratio <= 0.5 ? pass('au début', 'présent') : warn('en fin de titre', 'présent au début');
    },
  },
  {
    key: SeoRuleKey.KW_IN_META_TITLE,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      if (!ctx.matcher) return null;
      const meta = ctx.input.meta_title?.trim();
      if (!meta) return fail('meta title absent', 'présent');
      const ratio = firstPositionRatio(textStems(meta), ctx.matcher, true);
      if (ratio === null) return fail('absent', 'présent', [meta]);
      return ratio <= 0.5 ? pass('au début') : warn('présent, pas au début');
    },
  },
  {
    key: SeoRuleKey.KW_IN_META_DESC,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      if (!ctx.matcher) return null;
      const meta = ctx.input.meta_description?.trim();
      if (!meta) return fail('meta description absente', 'présent');
      // Matching souple et simple avertissement (2026.09.3) : le rédacteur reformule la requête.
      return textContainsKeyword(meta, ctx.matcher, true) ? pass('présent') : warn('absent', 'présent');
    },
  },
  {
    key: SeoRuleKey.KW_IN_SLUG,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      if (!ctx.matcher) return null;
      const slug = ctx.input.slug?.trim();
      if (!slug) return null;
      return textContainsKeyword(slug.replace(/[-_/]+/g, ' '), ctx.matcher)
        ? pass('présent')
        : fail('absent', 'présent', [slug]);
    },
  },
  {
    key: SeoRuleKey.KW_IN_INTRO,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      if (!ctx.matcher) return null;
      const first = ctx.bodyStems.slice(0, T.KW_INTRO_WORDS_WARN);
      if (countKeyword(first.slice(0, T.KW_INTRO_WORDS), ctx.matcher, true) > 0)
        return pass(`dans les ${T.KW_INTRO_WORDS} premiers mots`);
      if (countKeyword(first, ctx.matcher, true) > 0)
        return warn(`dans les ${T.KW_INTRO_WORDS_WARN} premiers mots`, `${T.KW_INTRO_WORDS} premiers mots`);
      return fail('absent de l’introduction', `${T.KW_INTRO_WORDS} premiers mots`);
    },
  },
  {
    key: SeoRuleKey.KW_IN_HEADINGS,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      if (!ctx.matcher) return null;
      const locations = headingsWithKeyword(ctx, ctx.matcher);
      const hits = locations;
      // Absent = avertissement, pas échec (2026.09.3) : le titre porte déjà le mot-clé et les
      // intertitres couvrent des sous-sujets ; l'exiger poussait au bourrage que le prompt interdit.
      if (hits.length === 0) return warn(0, `1–${T.KW_HEADINGS_MAX} titres`);
      if (hits.length > T.KW_HEADINGS_MAX) return warn(hits.length, `1–${T.KW_HEADINGS_MAX} titres`, locations);
      return pass(hits.length, `1–${T.KW_HEADINGS_MAX} titres`, locations);
    },
  },
  {
    key: SeoRuleKey.KW_DENSITY,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      if (!ctx.matcher) return null;
      if (ctx.wordCount === 0) return fail('0 %', `${T.KW_DENSITY.PASS[0]}–${T.KW_DENSITY.PASS[1]} %`);
      const density = (ctx.keywordCount / ctx.wordCount) * 100;
      if (ctx.matcher.stems.length >= T.KW_LONG.STEMS) {
        const max = Math.max(T.KW_LONG.MAX_FLOOR, Math.floor(ctx.wordCount / T.KW_LONG.MAX_PER_WORDS));
        const count = ctx.keywordCount;
        const range = `${T.KW_LONG.MIN}–${max} occurrences`;
        if (density > T.KW_DENSITY.BLOCK_ABOVE) return fail(`${count}×`, range, undefined, true);
        if (count === 0) return fail('0×', range);
        if (count < T.KW_LONG.MIN || count > max) return warn(`${count}×`, range);
        return pass(`${count}×`, range);
      }
      const value = `${Math.round(density * 10) / 10} % (${ctx.keywordCount}×)`;
      const threshold = `${T.KW_DENSITY.PASS[0]}–${T.KW_DENSITY.PASS[1]} %`;
      if (density > T.KW_DENSITY.BLOCK_ABOVE) return fail(value, threshold, undefined, true);
      if (density >= T.KW_DENSITY.PASS[0] && density <= T.KW_DENSITY.PASS[1]) return pass(value, threshold);
      if (density >= T.KW_DENSITY.WARN[0] && density <= T.KW_DENSITY.WARN[1]) return warn(value, threshold);
      return fail(value, threshold);
    },
  },
  {
    key: SeoRuleKey.KW_IN_IMAGE_ALT,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      if (!ctx.matcher) return null;
      if (ctx.images.length === 0 || ctx.options.images_pending) return null;
      const matcher = ctx.matcher;
      const hits = ctx.images.filter((i): boolean => !!i.alt && textContainsKeyword(i.alt, matcher, true)).length;
      if (hits === 1) return pass(1, '1 alt');
      if (hits === 0) return warn(0, '1 alt');
      return warn(hits, '1 alt');
    },
  },
  {
    key: SeoRuleKey.KW_STUFFING,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      if (!ctx.matcher || ctx.wordCount === 0) return null;
      const exact = countExactPhrase(ctx.fullTextNormalized, ctx.matcher.normalized);
      const passMax = Math.max(1, Math.floor(ctx.wordCount / T.KW_STUFFING_PASS_PER_WORDS));
      const failAbove = Math.max(2, Math.floor(ctx.wordCount / T.KW_STUFFING_FAIL_PER_WORDS));
      const threshold = `≤ ${passMax} répétitions exactes`;
      if (exact <= passMax) return pass(exact, threshold);
      if (exact <= failAbove) return warn(exact, threshold);
      return fail(exact, threshold);
    },
  },
  {
    key: SeoRuleKey.SECONDARY_KW_COVERAGE,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const terms = ctx.options.secondary_keywords ?? ctx.brief?.secondary_keywords ?? [];
      if (terms.length === 0) return null;
      // Souple depuis 2026.09.5 : « l'acompte versé lors de la réservation » couvre « acompte
      // réservation en ligne » à une racine près ; le strict faisait coller l'expression brute.
      const cov = termCoverage(ctx.stems, terms, true);
      const threshold = `≥ ${pct(T.SECONDARY_COVERAGE.PASS)}`;
      const value = `${pct(cov.ratio)} (${cov.found.length}/${terms.length})`;
      if (cov.ratio >= T.SECONDARY_COVERAGE.PASS) return pass(value, threshold, cov.missing);
      if (cov.ratio >= T.SECONDARY_COVERAGE.WARN) return warn(value, threshold, cov.missing);
      return fail(value, threshold, cov.missing);
    },
  },
];

/** Utilitaire exporté pour les tests : normalisation identique à celle des règles. */
export const normalizeForRules = normalizeFr;
